/* La vista della pagina di un dispaccio: notizia, caso o report.

   Il riferimento sono i tre disegni del 15/10/2026
   (uploads/Pagina della aperto notizia-html.zip,
   uploads/Notizia articolo chiuso riservata-html.zip,
   uploads/Caso OI Prima pagina-html.zip): la stessa pagina in tre stati.
   Aperto o chiuso lo decide `item.free`, come sempre; notizia, caso o
   report lo decide questo file, contando gli articoli collegati.

   «Collegati» sono le voci che arrivano da monte: le `case-histories`
   con lo stesso `agenzia_id` del dispaccio (`listCaseHistoriesByAgenziaId`
   in lib/nd-client.ts). Non è un legame redazionale, che a monte non
   esiste ancora: è il filone della stessa storia. L'ultima voce della
   cronologia coincide col `case` vivo (stesso titolo e stessa
   `datetime`, verificato su 165/1457 e su 352992/5570), quindi va
   scartata, altrimenti il dispaccio conterebbe sé stesso due volte.

   La classificazione è **solo di presentazione**. Non tocca il tipo di
   monte (`PRIORITY_STEM`, `PRIORITY_KEYS`, la targa): un Focus con
   cinque collegati resta Focus nella targa e si presenta come report.

   Modulo senza direttiva e senza fetch: lo leggono la pagina (Server
   Component), il componente della pagina (client) e `TIPO_DERIVATO` in
   lib/constants.ts. Una costante esportata da un file "use client" e
   letta dal server non è il suo valore (vedi LEAD_DISPACCI nei
   learnings). */

/** Come si presenta un dispaccio. */
export type VistaDispaccio = "notizia" | "caso" | "report";

/** Da quanti pezzi (il dispaccio più i collegati distinti) comincia un
 *  caso, e da quanti un report. Dal 21/10/2026 (regola della redazione):
 *  `n ≤ 2` è una notizia, `3 ≤ n ≤ 8` un caso, `9 ≤ n ≤ 15` un report.
 *  Oltre 15 la regola non dice niente: resta report (il filone più lungo,
 *  Corridoio CPEC, ne ha 29). Fino al 21/10/2026 erano `2 ≤ n ≤ 3` caso e
 *  `n ≥ 4` report.
 *
 *  Sono anche le soglie del tipo derivato stampato nelle liste
 *  (`TIPO_DERIVATO` le legge da qui): la riga del rullo e la pagina che
 *  apre devono chiamare il dispaccio nello stesso modo. Là si contano le
 *  voci di cronologia, che sono `n` quando l'ultima coincide col pezzo
 *  vivo, cioè sempre nei filoni misurati. */
export const CASO_DA = 3;
export const REPORT_DA = 9;

export function vistaDi(n: number): VistaDispaccio {
  if (n >= REPORT_DA) return "report";
  if (n >= CASO_DA) return "caso";
  return "notizia";
}

/** L'etichetta della vista: «Il caso» nel chip e nei titoli, «Report»
 *  quando il filone è lungo. La notizia non ne ha. */
export const ETICHETTA_VISTA: Record<Exclude<VistaDispaccio, "notizia">, string> = {
  caso: "Il caso",
  report: "Report",
};

/** Quante tappe della cronologia si vedono prima di «Mostra tutte». Il
 *  filone più lungo ne ha 29 (Corridoio CPEC, dietro il dispaccio 165):
 *  aperte tutte sono quattro schermate di date prima della colonna delle
 *  entità. Le visibili sono le più recenti, cioè quelle vicine al
 *  dispaccio che si sta leggendo. */
export const TAPPE_VISIBILI = 6;

/** Una data già scritta dal server nel fuso di Roma (formattata nel
 *  browser darebbe un'altra stringa e un errore d'idratazione). */
export type DataScritta = { iso: string; testo: string };

/** Un articolo collegato: una voce della cronologia diversa dal pezzo
 *  vivo. Porta solo titolo, data e sommario, anche quando il caso è
 *  aperto: il sommario è il `lead` (260 caratteri), cioè la parte già
 *  pubblica di qualunque dispaccio, e il resto della versione non lascia
 *  il server. */
export type Collegato = {
  id: string;
  titolo: string;
  /** `/article/history/[historyId]/[slug]`, come in ArticleHistory. */
  href: string;
  data: DataScritta;
  sommario: string;
  /** Unix ms, per l'ordine e per il conto dei mesi. */
  time: number;
};

/** Il minimo di una voce di cronologia che serve a contare. */
type Voce = { id: string; title: string; time: number };

function chiave(titolo: string): string {
  return titolo.normalize("NFKC").replace(/\s+/g, " ").trim().toLowerCase();
}

/** Le voci della cronologia che sono davvero altri articoli: senza la
 *  voce che coincide col pezzo vivo (stesso titolo e stessa data) e
 *  senza doppioni fra loro (stesso titolo e stessa data su due id: a
 *  monte capita quando una versione viene salvata due volte). L'ordine
 *  resta quello in ingresso. */
export function vociCollegate<T extends Voce>(voci: T[], vivo: { title: string; time: number }): T[] {
  const visti = new Set<string>([`${vivo.time}|${chiave(vivo.title)}`]);
  const out: T[] = [];
  for (const v of voci) {
    const k = `${v.time}|${chiave(v.title)}`;
    if (visti.has(k)) continue;
    visti.add(k);
    out.push(v);
  }
  return out;
}

/** `n`: il dispaccio più i suoi collegati distinti. */
export function pezziDelCaso(collegati: number): number {
  return 1 + collegati;
}

/** I numeri del riquadro «Il lavoro dietro il caso», tutti contati sui
 *  dati del dispaccio: niente cifre scritte a mano. */
export type CifreCaso = {
  /** Mesi di calendario fra la prima tappa e il dispaccio, almeno uno. */
  mesi: number;
  tappe: number;
  soggetti: number;
  luoghi: number;
};

/** Quello che la pagina riceve dal server (`datiCaso` in
 *  lib/caso-dati.ts). */
export type DatiCaso = {
  vista: VistaDispaccio;
  /** `n`: il dispaccio più i collegati distinti. */
  pezzi: number;
  /** Dal più vecchio al più recente, senza il pezzo vivo. */
  collegati: Collegato[];
  /** La data della prima tappa del filone (il dispaccio stesso, se è il
   *  più vecchio). `null` sulla notizia. */
  dal: DataScritta | null;
  /** La data del dispaccio, per la sua tappa nella cronologia. */
  questo: DataScritta;
  cifre: CifreCaso | null;
};

/** Mesi di calendario coperti dal filone, contando quello di partenza e
 *  quello d'arrivo: dal 21 agosto al 14 ottobre sono tre mesi
 *  osservati, non uno virgola otto. Legge solo le date del filone, non
 *  l'orologio, così server e browser danno lo stesso numero. */
export function mesiOsservati(primaMs: number, ultimaMs: number): number {
  const a = new Date(primaMs);
  const b = new Date(ultimaMs);
  const mesi = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth()) + 1;
  return Math.max(1, mesi);
}
